import type { CSSProperties } from "react";

/* ─────────────────────────────────────────────────────────
 * SYNTAX THEMES
 *
 * ChatGPT-style palette. The dark values are the ones this component already
 * used (the old `customAtomOneDark` override block) -- blue keywords, green
 * strings, pink numbers, orange built-ins -- with two contrast fixes noted
 * below. The light values are the same hues darkened to stay legible on a
 * light surface; ChatGPT has no published light token set.
 *
 * Previously only the dark half was defined and light mode fell back to stock
 * atomOneLight, which is a completely different palette -- that is why the two
 * themes never looked related.
 *
 * Both variants are generated from one builder, so they cannot drift apart
 * structurally. To retune, edit a palette below; the mapping is shared.
 * ───────────────────────────────────────────────────────── */

interface Palette {
  /** identifiers, punctuation, operators -- the default text colour */
  fg: string;
  /** comments */
  comment: string;
  /** keywords, literals (True/False/None), types */
  keyword: string;
  /** strings and regexes */
  string: string;
  /** numbers */
  number: string;
  /** function and class names being declared */
  title: string;
  /** built-in functions (print, len, ...) */
  builtin: string;
  /** attribute names -- HTML attributes, JSON keys, CSS selectors */
  attr: string;
}

const DARK: Palette = {
  fg: "#e5e5e5",
  comment: "#8b8b8b",
  keyword: "#2e95d3",
  string: "#00a67d",
  number: "#df3079",
  title: "#f22c3d",
  builtin: "#ff9800",
  attr: "#2e95d3",
};

const LIGHT: Palette = {
  fg: "#1f2328",
  comment: "#5b6270",
  keyword: "#0a63ab",
  string: "#05745a",
  number: "#b3186d",
  title: "#b02a37",
  builtin: "#9a4708",
  attr: "#0a63ab",
};

const buildTheme = (p: Palette): Record<string, CSSProperties> => ({
  hljs: { color: p.fg, background: "transparent" },

  "hljs-comment": { color: p.comment, fontStyle: "italic" },
  "hljs-quote": { color: p.comment, fontStyle: "italic" },
  "hljs-meta": { color: p.comment },

  "hljs-keyword": { color: p.keyword },
  "hljs-literal": { color: p.keyword },
  "hljs-type": { color: p.keyword },
  "hljs-symbol": { color: p.keyword },
  "hljs-doctag": { color: p.keyword },
  "hljs-template-tag": { color: p.keyword },
  "hljs-meta-keyword": { color: p.keyword },
  "hljs-selector-tag": { color: p.keyword },
  "hljs-link": { color: p.keyword, textDecoration: "underline" },

  "hljs-string": { color: p.string },
  "hljs-regexp": { color: p.string },
  "hljs-meta-string": { color: p.string },
  "hljs-formula": { color: p.string },
  "hljs-addition": { color: p.string },

  "hljs-number": { color: p.number },
  "hljs-bullet": { color: p.number },

  "hljs-title": { color: p.title, fontWeight: "bold" },
  "hljs-class": { color: p.title },
  "hljs-section": { color: p.title, fontWeight: "bold" },
  "hljs-name": { color: p.title },
  "hljs-deletion": { color: p.title },

  "hljs-built_in": { color: p.builtin },
  "hljs-builtin-name": { color: p.builtin },

  "hljs-attr": { color: p.attr },
  "hljs-attribute": { color: p.attr },
  "hljs-selector-id": { color: p.attr },
  "hljs-selector-class": { color: p.attr },
  "hljs-selector-attr": { color: p.attr },
  "hljs-selector-pseudo": { color: p.attr },

  "hljs-variable": { color: p.fg },
  "hljs-template-variable": { color: p.fg },
  "hljs-params": { color: p.fg },
  "hljs-subst": { color: p.fg },
  "hljs-tag": { color: p.fg },

  "hljs-emphasis": { fontStyle: "italic" },
  "hljs-strong": { fontWeight: "bold" },
});

export const syntaxLight = buildTheme(LIGHT);
export const syntaxDark = buildTheme(DARK);
